'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useSchedulerStoreContext } from "../../use-scheduler-store-context/index.mjs";
import { schedulerEventSelectors, schedulerOccurrencePlaceholderSelectors } from "../../scheduler-selectors/index.mjs";
import { isInternalDragOrResizePlaceholder } from "./drag-utils.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
export function useDropTarget(parameters) {
  const {
    surfaceType,
    ref,
    resourceId = null,
    getEventDropData,
    isValidDropTarget,
    addPropertiesToDroppedEvent
  } = parameters;
  const adapter = useAdapterContext();
  const store = useSchedulerStoreContext();
  React.useEffect(() => {
    if (!ref.current) {
      return undefined;
    }
    const getDataFromInside = (data, newStart, newEnd) => {
      const type = data.source === 'CalendarGridDayEventResizeHandler' || data.source === 'CalendarGridTimeEventResizeHandler' ? 'internal-resize' : 'internal-drag';
      return {
        type,
        surfaceType,
        start: newStart,
        end: newEnd,
        eventId: data.eventId,
        occurrenceKey: data.occurrenceKey,
        originalOccurrence: data.originalOccurrence,
        resourceId: resourceId === undefined ? data.originalOccurrence.resource ?? null : resourceId
      };
    };
    const getDataFromOutside = (data, start) => {
      const eventCreationConfig = schedulerEventSelectors.creationConfig(store.state);
      if (eventCreationConfig === false) {
        return undefined;
      }
      return {
        type: 'external-drag',
        surfaceType,
        start,
        // TODO: Improve the start and end time of a non all-day event dropped in the Month View.
        end: adapter.addMinutes(start, data.eventData.duration ?? eventCreationConfig.duration),
        eventData: data.eventData,
        onEventDrop: data.onEventDrop,
        resourceId: resourceId === undefined ? data.eventData.resource ?? null : resourceId
      };
    };
    return dropTargetForElements({
      element: ref.current,
      getData: () => ({
        isSchedulerDropTarget: true,
        surfaceType
      }),
      canDrop: ({
        source
      }) => {
        if (!isValidDropTarget(source.data)) {
          return false;
        }
        if (source.data.source === 'StandaloneEvent' && !schedulerEventSelectors.canDragEventsFromTheOutside(store.state)) {
          return false;
        }
        return true;
      },
      onDrag: ({
        source,
        location
      }) => {
        const newPlaceholder = getEventDropData({
          data: source.data,
          getDataFromInside,
          getDataFromOutside,
          input: location.current.input
        });
        if (newPlaceholder) {
          store.setOccurrencePlaceholder(newPlaceholder);
        }
      },
      onDrop: ({
        source,
        location
      }) => {
        const dropData = getEventDropData({
          data: source.data,
          getDataFromInside,
          getDataFromOutside,
          input: location.current.input
        });
        const placeholder = dropData ?? schedulerOccurrencePlaceholderSelectors.value(store.state);
        if (isInternalDragOrResizePlaceholder(placeholder)) {
          applyInternalDragOrResizeOccurrencePlaceholder(store, placeholder, addPropertiesToDroppedEvent);
        } else if (placeholder?.type === 'external-drag') {
          applyExternalDragOccurrencePlaceholder(store, placeholder, addPropertiesToDroppedEvent);
        }
      },
      onDragLeave: () => {
        const currentPlaceholder = schedulerOccurrencePlaceholderSelectors.value(store.state);
        if (currentPlaceholder?.surfaceType !== surfaceType) {
          return;
        }
        const type = currentPlaceholder.type;
        const shouldHidePlaceholder = type === 'external-drag' || isInternalDragOrResizePlaceholder(currentPlaceholder) && schedulerEventSelectors.canDropEventsToTheOutside(store.state);
        if (shouldHidePlaceholder) {
          store.setOccurrencePlaceholder(_extends({}, currentPlaceholder, {
            isHidden: true
          }));
        }
      }
    });
  }, [ref, surfaceType, resourceId, getEventDropData, isValidDropTarget, addPropertiesToDroppedEvent, adapter, store]);
}
/**
 * Applies the data from the placeholder occurrence to the event it represents.
 */
function applyInternalDragOrResizeOccurrencePlaceholder(store, placeholder, addPropertiesToDroppedEvent) {
  // TODO: Try to do a single state update.
  store.setOccurrencePlaceholder(null);
  const {
    eventId,
    start,
    end,
    originalOccurrence
  } = placeholder;
  const adapter = store.state.adapter;
  const changes = {
    id: eventId,
    start,
    end
  };

  // If `undefined`, we want to set the event resource to `undefined` (no resource).
  // If `null`, we want to keep the original event resource.
  if (placeholder.resourceId !== null) {
    changes.resource = placeholder.resourceId;
  }
  const additionalChanges = addPropertiesToDroppedEvent?.() ?? {};
  Object.assign(changes, additionalChanges);
  const hasChanged = Object.entries(changes).some(([key, value]) => {
    if (key === 'id') {
      return false;
    }
    if (key === 'start' || key === 'end') {
      return !adapter.isEqual(originalOccurrence.displayTimezone[key].value, value);
    }
    return originalOccurrence[key] !== value;
  });
  if (!hasChanged) {
    return;
  }
  if (originalOccurrence.displayTimezone.rrule) {
    store.updateRecurringEvent({
      occurrenceStart: originalOccurrence.displayTimezone.start.value,
      changes
    });
    return;
  }
  store.updateEvent(changes);
}
function applyExternalDragOccurrencePlaceholder(store, placeholder, addPropertiesToDroppedEvent) {
  const event = _extends({
    start: placeholder.start,
    end: placeholder.end
  }, placeholder.eventData);

  // If `undefined`, we want to set the event resource to `undefined` (no resource).
  // If `null`, we want to keep the original event resource.
  if (placeholder.resourceId !== null) {
    event.resource = placeholder.resourceId;
  }
  if (addPropertiesToDroppedEvent) {
    Object.assign(event, addPropertiesToDroppedEvent());
  }
  store.setOccurrencePlaceholder(null);
  store.createEvent(event);
  placeholder.onEventDrop?.();
}